import type { LoggerEvent } from '@/types/logger.types'
import { isIframe, random, timer } from '@/utils/helpers'
import { loadScript } from '@/utils/loadScript'

import { AdConsoleLogger } from './utils/ad-console-logger'

/**
 * 'none' - no GPP CMP at all
 * 'inapplicable' - a CMP is there, but it reports no section for this user (EU under Funding Choices)
 * 'applicable' - a CMP with a real section, safe to hand to prebid
 */
export type GppCmpState = 'none' | 'inapplicable' | 'applicable'

export type ActiveCmps = {
    gpp: GppCmpState
    tcf: boolean
}

export function hasActiveCmp(activeCmps: ActiveCmps): boolean {
    return activeCmps.gpp !== 'none' || activeCmps.tcf
}

type ConsentSource =
    | 'funding_choices'
    | 'existing_tcf'
    | 'existing_gpp'
    | 'existing_tcf_gpp'
    | 'mock'
    | 'no_cmp'
    | 'disabled'
    | 'error'

export type ConsentManagerOptions = {
    consentAccount: string
    handleConsentlessEnv: () => Promise<boolean>
    logEvent: (event: LoggerEvent) => void
    skipCmp?: boolean
    nativeConsentReady?: Promise<void>
}

/** Visibility of the CMP consent dialog. `unknown` until install() figures out which CMP (if any) is present. */
export type ConsentUiState = 'unknown' | 'visible' | 'hidden'

type ConsentUiStateListener = (state: ConsentUiState) => void

export enum GoogleFcAdBlockerStatus {
    UNKNOWN = 0,
    EXTENSION_LEVEL_AD_BLOCKER = 1,
    NETWORK_LEVEL_AD_BLOCKER = 2,
    NO_AD_BLOCKER = 3,
}

export interface GoogleFC {
    controlledLoading?: boolean
    callbackQueue?: { [event: string]: () => void }[]
    showRevocationMessage?: () => void
    getAdBlockerStatus?: () => number
    AdBlockerStatusEnum?: Record<string, GoogleFcAdBlockerStatus>
}

type GppState = {
    gppVersion: string
    cmpStatus: string
    cmpDisplayStatus: string
    signalStatus: string
    cmpId: number
    supportedAPIs: string[]
    sectionList: number[]
    applicableSections: number[]
    gppString: string
    parsedSections: Record<string, Record<string, unknown>[]>
}

type GppCallback = (result: unknown, success: boolean) => void

/**
 * A GPP section id is a positive integer (7 = usnat, 8+ = us states). `[]` means the CMP has
 * nothing for this user, `[-1]` means it cannot tell - neither is a signal worth forwarding.
 */
const hasApplicableGppSection = (applicableSections: unknown): boolean =>
    Array.isArray(applicableSections) &&
    applicableSections.some((section) => typeof section === 'number' && section > 0)

declare global {
    interface Window {
        __gpp?: (command: string, callback: GppCallback, parameter?: unknown, version?: unknown) => void
        __uspapi?: (command: string, version: unknown, callback: GppCallback) => void
        googlefc?: GoogleFC
    }
}

export class ConsentManager {
    private readonly PROBE_TIMEOUT = 1000
    private readonly PROBE_CALL_ID = 'pg-probe'
    private readonly LOCATOR_POLL_INTERVAL_MS = 50
    private readonly LOCATOR_POLL_TIMEOUT_MS = 500
    /**
     * A CMP that never answers must not park the ui state on `unknown` forever - everything waiting
     * on the dialog (notification queue, sign-in prompt) would then never run.
     */
    private readonly UI_STATE_FALLBACK_MS = 1500

    private resolveReady = () => {}
    ready: Promise<void>

    consentSource: ConsentSource | null = null

    private uiState: ConsentUiState = 'unknown'
    private uiStateListeners = new Set<ConsentUiStateListener>()
    private uiStateFallbackTimer?: ReturnType<typeof setTimeout>

    adConsoleLogger = new AdConsoleLogger('consent')

    private uspString = '1YNN'

    private usnatState: Record<string, unknown> = {
        Version: 1,
        SharingNotice: 1,
        SaleOptOutNotice: 1,
        SharingOptOutNotice: 1,
        TargetedAdvertisingOptOutNotice: 1,
        SensitiveDataProcessingOptOutNotice: 0,
        SensitiveDataLimitUseNotice: 0,
        SaleOptOut: 2,
        SharingOptOut: 2,
        TargetedAdvertisingOptOut: 2,
        SensitiveDataProcessing: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        KnownChildSensitiveDataConsents: [0, 0],
        PersonalDataConsents: 0,
        MspaCoveredTransaction: 2,
        MspaOptOutOptionMode: 0,
        MspaServiceProviderMode: 0,
    }

    private gppState: GppState = {
        gppVersion: '1.1',
        cmpStatus: 'loaded',
        cmpDisplayStatus: 'hidden',
        signalStatus: 'ready',
        supportedAPIs: ['7:usnat'],
        cmpId: 300,
        sectionList: [7],
        applicableSections: [7],
        gppString: 'DBABL~BVQqAAAAAg',
        parsedSections: {
            usnat: [this.usnatState],
        },
    }

    private subscribedTcf: Window['__tcfapi']
    private subscribedGpp: Window['__gpp']
    private partnerProbe: Promise<ActiveCmps> | undefined
    private partnerParentProbe: Promise<void> | undefined
    private partnerParentCmps: ActiveCmps = { gpp: 'none', tcf: false }

    private options: ConsentManagerOptions

    constructor(options: ConsentManagerOptions) {
        this.options = options
        ;({ promise: this.ready, resolve: this.resolveReady } = Promise.withResolvers())
        // armed here rather than after install() so that an install that never settles is covered too
        if (!options.nativeConsentReady) {
            this.uiStateFallbackTimer = setTimeout(() => this.setConsentUiState('hidden'), this.UI_STATE_FALLBACK_MS)
        }
    }

    public async install(cmpSource: 'playgama' | 'partner' = 'playgama'): Promise<ActiveCmps> {
        try {
            this.adConsoleLogger.debug('install')

            // the `finally` below still runs: `ready` resolves and the ui state settles on `hidden`,
            // so everything gated on the CMP (provider init, notification queue) proceeds as usual
            if (this.options.skipCmp) {
                this.adConsoleLogger.debug('install: skipped, no CMP in this environment')
                this.consentSource = 'disabled'
                await this.options.nativeConsentReady
                return { gpp: 'none', tcf: false }
            }

            if (cmpSource === 'partner') {
                if (isIframe()) {
                    await this.probePartnerParentCmps()
                }
                return await this.detectPartnerCmps()
            }

            const isMainFrame = !isIframe()

            if (isMainFrame) {
                const result = await loadScript({
                    src: `https://fundingchoicesmessages.google.com/i/${this.options.consentAccount}?ers=3`,
                    scriptName: 'Funding Choices',
                    useExisting: true,
                })
                if (result.status !== 'ok') {
                    this.adConsoleLogger.error('install: error', result.message)
                    this.consentSource = 'error'
                    return { gpp: 'none', tcf: false }
                }
                this.consentSource = 'funding_choices'
                this.adConsoleLogger.debug('install: success')
                const [gpp, tcf] = await Promise.all([this.waitForLocator('__gpp'), this.waitForLocator('__tcfapi')])
                return { gpp: gpp ? await this.probeGppState() : 'none', tcf }
            }

            const [isConsentless, hasGppLocator, hasTcfLocator] = await Promise.all([
                this.options.handleConsentlessEnv(),
                this.probeGppLocator(),
                this.probeTcfLocator(),
            ])

            if (hasTcfLocator && hasGppLocator) {
                this.consentSource = 'existing_tcf_gpp'
                this.adConsoleLogger.debug('existing CMP detected', this.consentSource)
            } else if (hasTcfLocator || hasGppLocator) {
                this.consentSource = hasTcfLocator ? 'existing_tcf' : 'existing_gpp'
                this.adConsoleLogger.debug('existing CMP detected', this.consentSource)
            } else if (isConsentless) {
                this.adConsoleLogger.debug('unable to install: consentless env detected but no CMP found')
                this.consentSource = 'mock'
                this.installUsPrivacyMock()
                this.adConsoleLogger.debug('mock installed')
                return { gpp: 'applicable', tcf: false }
            } else {
                this.consentSource = 'no_cmp'
            }

            return {
                gpp: hasGppLocator ? await this.probeGppState() : 'none',
                tcf: hasTcfLocator,
            }
        } catch (e) {
            this.adConsoleLogger.error('install: error', e instanceof Error ? (e as Error).message : String(e))
            this.consentSource = 'error'
            return { gpp: 'none', tcf: false }
        } finally {
            this.logInitEvent()
            this.resolveReady()
            this.subscribeConsentChanges()
        }
    }

    /** Read existing partner APIs only; never install Funding Choices or privacy mocks. */
    public detectPartnerCmps(): Promise<ActiveCmps> {
        if (this.partnerProbe) {
            return this.partnerProbe
        }
        this.partnerProbe = this.probePartnerCmps().finally(() => {
            this.partnerProbe = undefined
        })
        return this.partnerProbe
    }

    private probePartnerParentCmps(): Promise<void> {
        if (this.partnerParentProbe) {
            return this.partnerParentProbe
        }
        const probes: Promise<void>[] = []
        if (this.partnerParentCmps.gpp !== 'applicable' && typeof window.__gpp !== 'function') {
            probes.push(
                this.probeParentWindowLocator('__gppCall', '__gppReturn', '1.1', (result) => {
                    const ping = result as { signalStatus?: string; applicableSections?: unknown } | null
                    if (ping?.signalStatus === 'ready') {
                        this.partnerParentCmps.gpp = hasApplicableGppSection(ping.applicableSections)
                            ? 'applicable'
                            : 'inapplicable'
                    }
                }).then((found) => {
                    if (found && this.partnerParentCmps.gpp === 'none') {
                        this.partnerParentCmps.gpp = 'inapplicable'
                    }
                }),
            )
        }
        if (!this.partnerParentCmps.tcf && typeof window.__tcfapi !== 'function') {
            probes.push(
                this.probeTcfLocator().then((found) => {
                    this.partnerParentCmps.tcf = found
                }),
            )
        }
        this.partnerParentProbe = Promise.all(probes)
            .then(() => {})
            .catch((error: unknown) => {
                this.adConsoleLogger.error('partner CMP probe failed', error)
            })
            .finally(() => {
                this.partnerParentProbe = undefined
            })
        return this.partnerParentProbe
    }

    private async probePartnerCmps(): Promise<ActiveCmps> {
        if (isIframe()) {
            // Parent APIs may be absent: discover them in the background, without delaying bids.
            void this.probePartnerParentCmps()
        }
        const gpp = typeof window.__gpp === 'function' || this.partnerParentCmps.gpp !== 'none'
        const tcf = typeof window.__tcfapi === 'function' || this.partnerParentCmps.tcf
        if (tcf && gpp) {
            this.consentSource = 'existing_tcf_gpp'
        } else if (tcf || gpp) {
            this.consentSource = tcf ? 'existing_tcf' : 'existing_gpp'
        } else {
            this.consentSource = 'no_cmp'
        }
        this.subscribeConsentChanges()
        let gppState: GppCmpState = this.partnerParentCmps.gpp
        if (typeof window.__gpp === 'function') {
            gppState = await this.probeGppState()
        }
        return { gpp: gppState, tcf }
    }

    public get consentUiState(): ConsentUiState {
        return this.uiState
    }

    /**
     * Subscribes to CMP dialog visibility changes. Calls the listener immediately with the current
     * state and returns an unsubscribe function.
     */
    public onConsentUiStateChange(listener: ConsentUiStateListener): () => void {
        this.uiStateListeners.add(listener)
        listener(this.uiState)

        return () => {
            this.uiStateListeners.delete(listener)
        }
    }

    private setConsentUiState(state: ConsentUiState): void {
        clearTimeout(this.uiStateFallbackTimer)

        if (this.uiState === state) {
            return
        }
        this.uiState = state
        this.uiStateListeners.forEach((listener) => listener(state))
    }

    private logInitEvent(): void {
        const detectedApis = [
            typeof window.__tcfapi === 'function' && 'tcf',
            typeof window.__gpp === 'function' && 'gpp',
            typeof window.__uspapi === 'function' && 'uspapi',
        ].filter(Boolean)

        const detectedApisStr = detectedApis.join(',') || 'none'
        const consentSource = this.consentSource || 'unknown'

        this.adConsoleLogger.debug(`init | source: ${consentSource} | detected APIs: ${detectedApisStr}`)
        this.options.logEvent({
            event: 'custom_event',
            eventName: 'consent_init',
            dimension1: consentSource,
            dimension2: detectedApisStr,
        })
    }

    private subscribeConsentChanges(): void {
        if (this.consentSource === 'funding_choices') {
            this.subscribeFundingChoices()
            this.subscribeTcf()
        }

        if (this.consentSource === 'existing_tcf' || this.consentSource === 'existing_tcf_gpp') {
            this.subscribeTcf()
        }

        if (this.consentSource === 'existing_gpp' || this.consentSource === 'existing_tcf_gpp') {
            this.subscribeGpp()
        }

        // without the TCF api there is no CMP dialog that could ever be shown
        if (typeof window.__tcfapi !== 'function') {
            this.setConsentUiState('hidden')
        }
    }

    private subscribeFundingChoices(): void {
        window.googlefc = window.googlefc ?? {}
        window.googlefc.callbackQueue = window.googlefc.callbackQueue ?? []
        window.googlefc.callbackQueue.push({
            CONSENT_API_READY: () => this.logConsentChange('loaded', 'CONSENT_API_READY'),
            // getTCData is deprecated/unreliable here - accept/decline comes from subscribeTcf's useractioncomplete.
            CONSENT_DATA_READY: () => this.logConsentChange('loaded', 'CONSENT_DATA_READY'),
        })
    }

    private subscribeTcf(): void {
        if (!window.__tcfapi || this.subscribedTcf === window.__tcfapi) {
            return
        }
        this.subscribedTcf = window.__tcfapi
        window.__tcfapi?.('addEventListener', 2, (tcData, success) => {
            if (!success || !tcData) {
                // the CMP cannot tell us about its dialog, so treat it as never shown
                this.setConsentUiState('hidden')
                return
            }

            switch (tcData.eventStatus) {
                case 'tcloaded': {
                    this.setConsentUiState('hidden')
                    this.logConsentChange('loaded', 'tcloaded')
                    break
                }
                case 'cmpuishown': {
                    this.setConsentUiState('visible')
                    this.logConsentChange('shown', 'cmpuishown')
                    break
                }
                case 'useractioncomplete': {
                    this.setConsentUiState('hidden')
                    this.logConsentChange(tcData.purpose?.consents?.[1] ? 'accepted' : 'declined', 'useractioncomplete')
                    break
                }
                default: {
                    this.setConsentUiState('hidden')
                }
            }
        })
    }

    private subscribeGpp(): void {
        if (!window.__gpp || this.subscribedGpp === window.__gpp) {
            return
        }
        this.subscribedGpp = window.__gpp
        window.__gpp?.('addEventListener', (result, success) => {
            const data = result as { eventName?: string; data?: string } | null
            if (success && data?.eventName === 'signalStatus' && data?.data === 'ready') {
                window.__gpp?.(
                    'getSection',
                    (section, sectionSuccess) => {
                        if (sectionSuccess) {
                            const usnat = (section as Record<string, unknown>[] | null)?.[0]
                            const hasConsent = usnat?.TargetedAdvertisingOptOut !== 1
                            this.logConsentChange(hasConsent ? 'accepted' : 'declined', 'signalStatus')
                        }
                    },
                    'usnat',
                )
            }
        })
    }

    private logConsentChange(label: string, rawEvent: string): void {
        this.adConsoleLogger.debug(`consent changed | event: ${rawEvent} | status: ${label}`)
        this.options.logEvent({
            event: 'custom_event',
            eventName: 'consent_change',
            dimension1: label,
            dimension2: rawEvent,
        })
    }

    private async waitForLocator(globalName: '__gpp' | '__tcfapi'): Promise<boolean> {
        const deadline = Date.now() + this.LOCATOR_POLL_TIMEOUT_MS
        while (typeof window[globalName] !== 'function') {
            if (Date.now() >= deadline) {
                return false
            }
            await timer(this.LOCATOR_POLL_INTERVAL_MS)
        }
        return true
    }

    private probeParentWindowLocator(
        callKey: string,
        returnKey: string,
        version: string | number,
        onResponse?: (result: unknown) => void,
    ): Promise<boolean> {
        return new Promise((resolve) => {
            let settled = false

            const handler = (event: MessageEvent): void => {
                const msg = event.data
                if (msg && msg[returnKey] && msg[returnKey].callId === this.PROBE_CALL_ID) {
                    settled = true
                    window.removeEventListener('message', handler)
                    onResponse?.(msg[returnKey].returnValue)
                    resolve(true)
                }
            }

            window.addEventListener('message', handler)

            try {
                window.parent.postMessage(
                    {
                        [callKey]: {
                            command: 'ping',
                            version,
                            callId: this.PROBE_CALL_ID,
                        },
                    },
                    '*',
                )
            } catch {
                // Silently handle postMessage errors
            }

            setTimeout(() => {
                if (!settled) {
                    window.removeEventListener('message', handler)
                    resolve(false)
                }
            }, this.PROBE_TIMEOUT)
        })
    }

    private probeGppLocator(): Promise<boolean> {
        return this.probeParentWindowLocator('__gppCall', '__gppReturn', '1.1')
    }

    /**
     * Ask the CMP whether any GPP section actually applies to this user.
     * for non-US users we should avoid to send regs.gpp: ""` + `regs.gpp_sid: [-1]
     */
    private probeGppState(): Promise<GppCmpState> {
        const gpp = window.__gpp

        if (typeof gpp !== 'function') {
            return Promise.resolve('inapplicable')
        }

        return new Promise((resolve) => {
            let settled = false
            let listenerId: number | undefined

            const removeListener = (): void => {
                if (listenerId !== undefined) {
                    try {
                        gpp('removeEventListener', () => {}, listenerId)
                    } catch {
                        // the CMP went away - nothing to clean up
                    }
                }
            }

            const settle = (state: Exclude<GppCmpState, 'none'>): void => {
                if (settled) {
                    return
                }
                settled = true
                clearTimeout(timeoutId)
                removeListener()
                this.adConsoleLogger.debug(`gpp state: ${state}`)
                resolve(state)
            }

            const timeoutId = setTimeout(() => settle('applicable'), this.LOCATOR_POLL_TIMEOUT_MS)

            // `applicableSections` is only trustworthy once the CMP resolved: until then `[-1]`
            // means "cannot determine yet" just as much as it means "nothing applies"
            const evaluate = (pingData: unknown): void => {
                const ping = pingData as { signalStatus?: string; applicableSections?: unknown } | null
                if (ping?.signalStatus === 'ready') {
                    settle(hasApplicableGppSection(ping.applicableSections) ? 'applicable' : 'inapplicable')
                }
            }

            try {
                gpp('ping', (result, success) => {
                    if (success) {
                        evaluate(result)
                    }
                })

                // the CMP had not resolved yet - wait for it rather than assume
                if (!settled) {
                    gpp('addEventListener', (result, success) => {
                        const event = result as { listenerId?: number; pingData?: unknown } | null
                        if (!success || !event) {
                            return
                        }
                        if (typeof event.listenerId === 'number') {
                            listenerId = event.listenerId
                        }
                        if (settled) {
                            removeListener()
                            return
                        }
                        evaluate(event.pingData)
                    })
                }
            } catch {
                settle('applicable')
            }
        })
    }

    private probeTcfLocator(): Promise<boolean> {
        return this.probeParentWindowLocator('__tcfapiCall', '__tcfapiReturn', 2)
    }

    private installUsPrivacyMock(): void {
        this.adConsoleLogger.debug('No CMP detected, installing PG US privacy mock')
        this.installLocator('__gppLocator')
        this.installLocator('__uspapiLocator')
        this.installGppApi()

        window.addEventListener('message', this.messageHandler, false)

        if (typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('gpploaded'))
            window.dispatchEvent(new CustomEvent('uspapiloaded'))
        }
    }

    private installLocator(name: string) {
        // @ts-ignore wrong types
        if (!window.frames[name]) {
            const iframe = document.createElement('iframe')
            iframe.name = name
            iframe.style.display = 'none'
            document.documentElement.appendChild(iframe)
        }
    }

    private installGppApi(): void {
        window.__gpp = (command, callback, parameter) => {
            this.adConsoleLogger.debug('__gpp', command)
            let result = null
            let success = true

            switch (command) {
                case 'ping':
                    result = {
                        gppVersion: this.gppState.gppVersion,
                        cmpStatus: this.gppState.cmpStatus,
                        cmpDisplayStatus: this.gppState.cmpDisplayStatus,
                        signalStatus: this.gppState.signalStatus,
                        supportedAPIs: this.gppState.supportedAPIs,
                        cmpId: this.gppState.cmpId,
                        gppString: this.gppState.gppString,
                        sectionList: this.gppState.sectionList,
                        applicableSections: this.gppState.applicableSections,
                    }
                    break
                case 'getGPPData':
                    result = this.gppState
                    break
                case 'hasSection':
                    result = parameter === 'usnat' || parameter === 7
                    break
                case 'getSection':
                    if (parameter === 'usnat' || parameter === 7) {
                        result = this.gppState.parsedSections.usnat
                    } else {
                        result = null
                    }
                    break
                case 'getField':
                    if (typeof parameter === 'string' && parameter.startsWith('usnat.')) {
                        const field = parameter.split('.')[1]!
                        result = this.usnatState[field] !== undefined ? this.usnatState[field] : null
                    } else {
                        result = null
                    }
                    break
                case 'addEventListener':
                    result = {
                        eventName: 'signalStatus',
                        listenerId: Math.floor(random() * 10000),
                        data: this.gppState.signalStatus,
                        pingData: {
                            gppVersion: this.gppState.gppVersion,
                            cmpStatus: this.gppState.cmpStatus,
                            cmpDisplayStatus: this.gppState.cmpDisplayStatus,
                            signalStatus: this.gppState.signalStatus,
                            supportedAPIs: this.gppState.supportedAPIs,
                            cmpId: this.gppState.cmpId,
                            gppString: this.gppState.gppString,
                            sectionList: this.gppState.sectionList,
                            applicableSections: this.gppState.applicableSections,
                        },
                    }
                    break
                case 'removeEventListener':
                    result = true
                    break
                default:
                    success = false
                    break
            }

            this.adConsoleLogger.debug(`__gpp response ("${command}")`, { result, success })

            if (typeof callback === 'function') {
                callback(result, success)
            }
        }

        window.__uspapi = (command, version, callback) => {
            this.adConsoleLogger.debug('__uspapi', { command, version })
            let result = null
            let success = false

            if (command === 'getUSPData') {
                result = { version: 1, uspString: this.uspString }
                success = true
            }

            this.adConsoleLogger.debug(`__uspapi response ("${command}")`, { result, success })
            if (typeof callback === 'function') {
                callback(result, success)
            }
        }
    }

    private messageHandler(event: MessageEvent) {
        let data
        try {
            data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        } catch (_e) {
            return
        }

        if (data && data.__gppCall) {
            const call = data.__gppCall
            window.__gpp?.(
                call.command,
                (result, success) => {
                    const returnMsg = {
                        __gppReturn: { returnValue: result, success, callId: call.callId },
                    }

                    if (event.source && typeof event.source.postMessage === 'function') {
                        event.source.postMessage(returnMsg, { targetOrigin: '*' })
                    }
                },
                call.parameter,
                call.version,
            )
        }

        if (data && data.__uspapiCall) {
            const call = data.__uspapiCall
            window.__uspapi?.(call.command, call.version, (result, success) => {
                const returnMsg = {
                    __uspapiReturn: { returnValue: result, success, callId: call.callId },
                }

                if (event.source && typeof event.source.postMessage === 'function') {
                    event.source.postMessage(returnMsg, { targetOrigin: '*' })
                }
            })
        }
    }
}
